import React from 'react';
import ChatInitModal from '../chat-init-modal/index';
import ChatWidget from '../../shared/chat-widget';
import './chatbot.css';
import { t, getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';
import { getSession } from './chat-bff-client';
import { createProtheusTransport } from './protheus-transport';
import { PendingAnswer } from './pending-answer';
import {
  FRONTEND_VERSION,
  trackChatInitialized,
  trackGateClosed,
  trackGateOpened,
  trackLauncherClick,
  trackMessageSent,
  trackPanelToggled,
  trackResponseFailed,
  trackResponseReceived,
  trackTurnCancelled,
} from './analytics';

/**
 * The 2610 chat: the in-house widget over this repository's BFF.
 *
 * WHAT REPLACED WHAT
 *
 * Gone: the `dta_chat.js` script tag, `<dta-chat>`, `<dta-chat-anchor>`, the
 * DTA API key that shipped in the bundle, the click interception that stopped DTA
 * opening before verification, the shadow-DOM style injection that repositioned
 * DTA's modal, and the MutationObserver plus 2-second interval that rewrote links
 * inside DTA's shadow root. All of it existed to bend a third-party web component
 * into behaving; none of it has an equivalent here, because the widget is a React
 * component whose open state, styling and rendering are props.
 *
 * Kept: the email + reCAPTCHA gate, the speech bubble, and every analytics event
 * (see analytics.js for the inventory and what changed about it).
 *
 * 2510 is untouched and still drives DTA.
 *
 * WHAT THE VISITOR WILL NOTICE
 *
 * DTA streamed token by token. This agent produces ONE complete answer at the end
 * of the turn, and a turn takes ~7s to ~139s with a 285s ceiling. That is a real
 * regression in perceived responsiveness and cannot be papered over, so the
 * mitigation is honest feedback rather than a fake progress bar: `PendingAnswer`
 * shows a live elapsed-seconds counter driven by the backend's `heartbeat` frames,
 * which carry elapsed time and nothing else.
 */
class Chatbot2610 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showSpeechBubble: false,
      showInitModal: false,
      // Whether the visitor holds a usable credential. The cookie is HttpOnly, so
      // this is the server's answer, never something the page decides for itself.
      verified: false,
      panelOpen: false,
      currentLanguage: getCurrentLanguage(),
      turnIndex: 0,
    };

    // One transport per mounted component, holding the widget-conversation to
    // server-chat-id mapping. Created here rather than in render so a re-render
    // cannot discard the mapping mid-conversation.
    this.chat = createProtheusTransport(this.handleTurnEvent);
    this.lastConversationId = null;
    this.sessionAbort = new AbortController();
  }

  componentDidMount() {
    this.setState({ showSpeechBubble: true });

    // A visitor who passed the gate earlier still holds the cookie, so a reload
    // must not send them back through the modal. Only the server can answer this.
    getSession(this.sessionAbort.signal)
      .then((session) => {
        if (session) this.setState({ verified: true });
      })
      .catch(() => {
        // Network trouble on a probe is not worth surfacing: the visitor simply
        // sees the gate, which is the correct fallback.
      });

    this.unsubscribe = useLanguageStore.subscribe((state) => {
      this.setState({ currentLanguage: state.currentLanguage });
    });
  }

  componentWillUnmount() {
    this.sessionAbort.abort();
    if (this.unsubscribe) this.unsubscribe();
  }

  /**
   * Analytics for one turn, reported by the transport.
   *
   * Cancellation only shows up here. `useChat` treats an abort as a normal
   * outcome and does not call `onError`, so a stopped turn is invisible to every
   * widget callback — and "how long do people wait before giving up" is one of the
   * two numbers that decide whether this response time is acceptable.
   */
  handleTurnEvent = (event) => {
    const turnIndex = this.state.turnIndex;
    if (event.type === 'sent') {
      trackMessageSent({ messageLength: event.messageLength, turnIndex: turnIndex + 1 });
      this.setState((prev) => ({ turnIndex: prev.turnIndex + 1 }));
    } else if (event.type === 'answered') {
      trackResponseReceived({
        durationMs: event.durationMs,
        answerLength: event.answerLength,
        turnIndex,
      });
    } else if (event.type === 'failed') {
      trackResponseFailed({
        code: event.code,
        requestId: event.requestId,
        durationMs: event.durationMs,
        turnIndex,
      });
    } else if (event.type === 'cancelled') {
      trackTurnCancelled({ elapsedMs: event.elapsedMs, turnIndex });
    }
  };

  /**
   * The launcher was clicked, or the panel was closed.
   *
   * `open` is controlled precisely so this can intercept. An unverified visitor
   * gets the gate and the panel stays shut — which is the same intent as the old
   * component's three layers of click interception on `<dta-chat-anchor>`, in one
   * branch instead of capture-phase listeners and shadow-DOM traversal.
   */
  handleOpenChange = (next) => {
    if (next) {
      trackLauncherClick({ chatInitialized: this.state.verified });
      if (!this.state.verified) {
        this.setState({ showInitModal: true });
        trackGateOpened();
        return;
      }
    }
    this.setState({ panelOpen: next });
    trackPanelToggled({ open: next });
  };

  handleCloseInitModal = () => {
    this.setState({ showInitModal: false });
    trackGateClosed();
  };

  /**
   * The gate was passed.
   *
   * There is no credential to store: the backend already set an HttpOnly cookie on
   * the verification response, which is why this receives an email and does
   * nothing with it beyond analytics. The old component had to reconfigure DTA with
   * an API key and a user id at this point.
   */
  handleInitializeChat = async ({ email }) => {
    trackChatInitialized(email);
    this.setState({
      verified: true,
      showInitModal: false,
      showSpeechBubble: false,
      panelOpen: true,
    });
  };

  /**
   * The widget changed conversation, which happens on "new chat".
   *
   * `reset()` has no server-side hook, so releasing the previous conversation is
   * the consumer's job. Skipping it would leave the old conversation alive on the
   * agent holding context and a session row that nothing would ever remove —
   * `DELETE /chat/conversations/{id}` is the only deletion path that exists.
   */
  handleConversationChange = (conversationId) => {
    const previous = this.lastConversationId;
    this.lastConversationId = conversationId;
    if (previous && previous !== conversationId) {
      void this.chat.releaseConversation(previous);
    }
  };

  handleWidgetError = (error) => {
    // Already reported with its code and request id by handleTurnEvent. Logged
    // rather than re-tracked so one failure is not counted twice.
    console.error('Chat error:', error && error.message);
  };

  widgetConfig = () => ({
    // Unused when `transport` is set, but kept meaningful: it is what appears in
    // devtools and in any future switch back to a built-in protocol.
    apiUrl: '/chat',
    transport: this.chat.transport,
    // The authentication scheme in one line: the visitor cookie, same-origin.
    credentials: 'include',
    // NO apiKey, deliberately. Whatever goes in this config object is readable in
    // devtools, which is how the previous integration ended up shipping a live
    // `x-dta-api-key` inside the bundle. Authentication here is the HttpOnly
    // cookie, so there is nothing to leak.
    // agent 285s < ingress 300s < client 310s. The widget default of 120000 would
    // abort roughly half of the slow-but-successful turns measured on this agent.
    timeoutMs: 310000,
    // Context lives server-side, keyed by chat_id. Sending history is not merely
    // wasteful here: the request schema is `{message}` with extra="forbid", so any
    // extra field is a guaranteed 422.
    historyLimit: 0,
  });

  render() {
    const { showSpeechBubble, showInitModal, currentLanguage, panelOpen } = this.state;

    return (
      // The class name is load-bearing for analytics: the generic click tracker in
      // components/global/google-tag-manager walks up the DOM looking for a class
      // containing "chatbot" to derive component_context. Renaming it silently
      // reclassifies every click in here as 'unknown'.
      <div
        className="chatbot-container"
        data-frontend-version={FRONTEND_VERSION}
        // Exposes whether the server recognised the visitor's cookie. The cookie
        // itself is HttpOnly and unreadable, so without this there is no way to
        // tell "gate not passed" from "cookie present but rejected" while
        // debugging a session — and it gives the tests something to wait on.
        data-chat-verified={this.state.verified ? 'true' : 'false'}
      >
        {showSpeechBubble && (
          <div className="speech-bubble">
            <div className="speech-bubble-content">
              <span className="speech-bubble-text">
                {t('chatbot_message_2610', currentLanguage)}
              </span>
            </div>
            <div className="speech-bubble-arrow"></div>
          </div>
        )}

        <ChatInitModal
          isOpen={showInitModal}
          onClose={this.handleCloseInitModal}
          onInitializeChat={this.handleInitializeChat}
        />

        <ChatWidget
          config={this.widgetConfig()}
          open={panelOpen}
          onOpenChange={this.handleOpenChange}
          title={t('chatbot_title_2610', currentLanguage)}
          // No upload path exists anywhere in the chain: the BFF has no endpoint
          // and the agent's request schema forbids extra fields.
          allowAttachments={false}
          // Answers embed third-party TDN content. The widget's DOMPurify
          // allow-list is what makes rendering it safe.
          markdown
          // Kept so a reload restores the transcript. The transport handles the
          // case a restored transcript outlives the server's memory of its
          // chat_id: a 404 creates a fresh conversation instead of dead-ending.
          persist
          storageKey="protheus-release-2610-chat"
          renderMessage={renderPendingAware}
          onError={this.handleWidgetError}
          onConversationChange={this.handleConversationChange}
        />
      </div>
    );
  }
}

/**
 * Show the elapsed counter while an answer is pending, and default rendering
 * otherwise.
 *
 * `renderMessage` replaces the bubble body for every message, so this has to hand
 * the non-pending cases back. Returning `undefined` is what does that: the widget's
 * MessageBubble falls through to its own markdown/error/plain-text branches when
 * `renderMessage` yields nothing for a message.
 */
function renderPendingAware(message) {
  const isPending = message.status === 'pending' || message.status === 'streaming';
  if (!isPending || message.role !== 'assistant' || message.content.trim() !== '') {
    return undefined;
  }
  return <PendingAnswer meta={message.meta} />;
}

export default Chatbot2610;
